import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import UserLayout from '../../components/user/UserLayout';
import { Wallet, TrendingUp, Users, Award, Server, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const StatCard = ({ icon: Icon, label, value, sub, color = 'primary', onClick }) => (
  <div onClick={onClick} className={`bg-dark-700 border border-white/5 rounded-2xl p-5 ${onClick ? 'cursor-pointer hover:border-primary-500/30 transition-colors' : ''}`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-gray-400 text-sm mt-0.5">{label}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

export default function UserDashboard() {
  const { user, refreshUser } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [profits, setProfits] = useState(null);
  const [copied, setCopied] = useState(false);
  const referralLink = `${window.location.origin}/register/${user?.referralCode}`;

  useEffect(() => {
    refreshUser();
    axios.get('/api/wallet/balance').then(r => setWallet(r.data)).catch(() => {});
    axios.get('/api/profit/history?limit=5').then(r => setProfits(r.data)).catch(() => {});
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  const LEVEL_CONDITIONS = {
    0: { direct: 0, team: 0, nextDirect: 3, nextTeam: 10 },
    1: { direct: 3, team: 10, nextDirect: 5, nextTeam: 50 },
    2: { direct: 5, team: 50, nextDirect: 5, nextTeam: 150 },
    3: { direct: 5, team: 150, nextDirect: 5, nextTeam: 500 },
    4: { direct: 5, team: 500, nextDirect: 5, nextTeam: 1500 },
    5: { direct: 5, team: 1500, nextDirect: 5, nextTeam: 5000 },
    6: { direct: 5, team: 5000, nextDirect: 5, nextTeam: 11000 },
    7: { direct: 5, team: 11000, nextDirect: null, nextTeam: null },
  };
  const currentLevelInfo = LEVEL_CONDITIONS[user?.level || 0];

  const getPercent = (value, target) => {
    if (!target || target === 0) return 100;
    return Math.round(Math.min(100, (value / target) * 100));
  };

  const calcLegProgress = () => {
    if (!currentLevelInfo?.nextTeam) return 100;
    const totalTeam = user?.teamSize || 0;
    const legs = user?.legs || [];
    if (legs.length === 0 || totalTeam === 0) return 0;

    const numLegs = legs.length;
    const maxLegPercent = 40;
    const maxAllowedPercent = numLegs === 1 ? 100 : numLegs === 2 ? 100 - maxLegPercent : maxLegPercent;
    const maxAllowed = Math.floor((totalTeam * maxAllowedPercent) / 100);
    const biggestLeg = Math.max(...legs.map((leg) => leg.legSize || 0), 0);
    if (biggestLeg === 0) return 100;

    return Math.round(Math.min(100, (maxAllowed / biggestLeg) * 100));
  };

  const directProgress = getPercent(user?.directReferrals || 0, currentLevelInfo?.nextDirect);
  const teamProgress = getPercent(user?.teamSize || 0, currentLevelInfo?.nextTeam);
  const legProgress = calcLegProgress();
  const overallProgress = currentLevelInfo?.nextTeam ? Math.min(directProgress, teamProgress, legProgress) : 100;

  return (
    <UserLayout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">Welcome back, {user?.name}!</p>
        </div>

        {/* Alerts */}
        {!user?.setupFeePaid && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-amber-300 font-medium">subcription fee pending</p>
              <p className="text-amber-400/70 text-sm">Pay $150 subcription fee to activate your trading account</p>
            </div>
            <a href="/setup-fee" className="ml-auto bg-amber-500 text-dark-900 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors flex-shrink-0">Pay Now</a>
          </div>
        )}

        {!user?.mt5Connected && user?.setupFeePaid && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <Server className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <div>
              <p className="text-blue-300 font-medium">Connect your MT5 account</p>
              <p className="text-blue-400/70 text-sm">Link your MetaTrader 5 credentials to start automated trading</p>
            </div>
            <a href="/mt5" className="ml-auto bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-500 transition-colors flex-shrink-0">Connect</a>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          <StatCard icon={Wallet} label="Main Wallet" value={`$${(wallet?.mainWallet || 0).toFixed(2)}`} sub="Service charges wallet" />
          <StatCard icon={TrendingUp} label="Commission Wallet" value={`$${(wallet?.commissionWallet || 0).toFixed(2)}`} sub="Withdraw earnings" color="green" />
          <StatCard icon={Users} label="Direct Referrals" value={user?.directReferrals || 0} sub="Level-1 referrals only" color="blue" />
          <StatCard icon={Users} label="Team Referrals" value={user?.teamReferrals || 0} sub={`Total team: ${user?.teamSize || 0}`} color="purple" />
          <StatCard icon={Award} label="Current Level" value={`Level ${user?.level || 0}`}
            sub={currentLevelInfo?.nextTeam ? `Next: ${currentLevelInfo.nextDirect} direct, ${currentLevelInfo.nextTeam} team` : 'Max Level!'} color="purple" />
        </div>

        {/* Overall Level Progress */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-white font-semibold">Overall Level Progress</h3>
              <p className="text-gray-400 text-sm mt-1">Includes direct referrals, team size and 40% leg distribution requirements.</p>
            </div>
            <span className="text-white font-bold text-lg">{overallProgress}%</span>
          </div>
          <div className="h-3 bg-dark-600 rounded-full overflow-hidden mt-4">
            <div className="h-full bg-gradient-to-r from-primary-500 to-blue-500 rounded-full transition-all" style={{ width: `${overallProgress}%` }}></div>
          </div>
          {/* <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-gray-400">
            <div className="bg-dark-600 rounded-xl px-3 py-2 text-center">Direct {directProgress}%</div>
            <div className="bg-dark-600 rounded-xl px-3 py-2 text-center">Team {teamProgress}%</div>
            <div className="bg-dark-600 rounded-xl px-3 py-2 text-center">Legs {legProgress}%</div>
          </div> */}
        </div>

        {/* Level Progress + MT5 Status */}
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          {/* Level Progress */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-4">Level Progress</h3>
            {/* <div className="space-y-6">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Direct Progress</span>
                  <span className="text-white font-medium">{currentLevelInfo?.nextDirect ? `${Math.round(Math.min(100, ((user?.directReferrals || 0) / currentLevelInfo.nextDirect) * 100))}%` : '100%'}</span>
                </div>
                <div className="h-2 bg-dark-600 rounded-full overflow-hidden">
                  <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${Math.min(100, ((user?.directReferrals || 0) / currentLevelInfo.nextDirect) * 100)}%` }}></div>
                </div>
                {currentLevelInfo?.nextDirect && (
                  <p className="text-xs text-gray-500 mt-2">{user?.directReferrals || 0} of {currentLevelInfo.nextDirect} direct referrals</p>
                )}
              </div>

              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Team Progress</span>
                  <span className="text-white font-medium">{currentLevelInfo?.nextTeam ? `${Math.round(Math.min(100, ((user?.teamSize || 0) / currentLevelInfo.nextTeam) * 100))}%` : '100%'}</span>
                </div>
                <div className="h-2 bg-dark-600 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, ((user?.teamSize || 0) / currentLevelInfo.nextTeam) * 100)}%` }}></div>
                </div>
                {currentLevelInfo?.nextTeam && (
                  <p className="text-xs text-gray-500 mt-2">{user?.teamSize || 0} of {currentLevelInfo.nextTeam} team referrals</p>
                )}
              </div>
            </div> */}
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[1,2,3,4,5,6,7].map(l => (
                <div key={l} className={`rounded-lg py-1.5 text-xs font-medium ${(user?.level || 0) >= l ? 'bg-primary-500/20 text-primary-300' : 'bg-dark-600 text-gray-500'}`}>
                  L{l} {(user?.level || 0) >= l ? '✓' : ''}
                </div>
              ))}
            </div>
          </div>

          {/* MT5 Status */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-4">MT5 Status</h3>
            <div className={`flex items-center gap-3 p-4 rounded-xl ${user?.mt5Connected ? 'bg-primary-500/10 border border-primary-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user?.mt5Connected ? 'bg-primary-500/20' : 'bg-red-500/20'}`}>
                <Server className={`w-5 h-5 ${user?.mt5Connected ? 'text-primary-400' : 'text-red-400'}`} />
              </div>
              <div>
                <p className={`font-medium ${user?.mt5Connected ? 'text-primary-300' : 'text-red-300'}`}>
                  {user?.mt5Connected ? 'Connected' : 'Not Connected'}
                </p>
                <p className="text-gray-400 text-sm">{user?.mt5Connected ? 'Trading bot is active' : 'Connect your MT5 account'}</p>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-400 mb-2">Profit deduction rate</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-3 bg-dark-600 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary-600 to-primary-400 rounded-full" style={{ width: '40%' }}></div>
                </div>
                <span className="text-white font-bold text-sm">40%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">40% of every profit is auto-deducted from your wallet</p>
            </div>
          </div>
        </div>

        {/* Referral Link */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
          <h3 className="text-white font-semibold mb-1">Your Referral Link</h3>
          <p className="text-gray-400 text-sm mb-4">Share this link to earn commission on every registration and profit</p>
          <div className="flex gap-3">
            <div className="flex-1 bg-dark-600 border border-white/10 rounded-xl px-4 py-3 text-gray-300 text-sm font-mono truncate">
              {referralLink}
            </div>
            <button onClick={copyLink} className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-3 rounded-xl flex items-center gap-2 transition-colors flex-shrink-0">
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
        </div>

        {/* Recent Profits */}
        {profits?.profits?.length > 0 && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-4">Recent Profits</h3>
            <div className="space-y-3">
              {profits.profits.map(p => (
                <div key={p._id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white text-sm font-medium">${p.grossProfit.toFixed(2)} profit</p>
                    <p className="text-gray-500 text-xs">{new Date(p.date).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-red-400 text-sm font-medium">-${p.deductionAmount.toFixed(2)}</p>
                    <p className="text-gray-500 text-xs">40% deducted</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </UserLayout>
  );
}
